// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * Locale-aware date display for the UI. date-fns `format(d, 'MMM d, yyyy')` is English whatever
 * the app locale ("Oct 4, 2026" on a Chinese screen). English keeps going through date-fns so its
 * output is byte-identical to before; every other locale uses Intl.
 *
 * NOT for the PDF / CSV exports (English-only documents; jsPDF's built-in fonts have no CJK glyphs)
 * or anywhere a machine-readable string is needed.
 */
import { format } from 'date-fns'
import { useCallback } from 'react'
import { useLocale } from 'react-admin'

export type DateStyle = 'short' | 'shortNoYear' | 'dayMonth' | 'long' | 'dateTime'

// The date-fns pattern each style replaces (English output).
const DATE_FNS: Record<DateStyle, string> = {
  short: 'MMM d, yyyy',
  shortNoYear: 'MMM d',
  dayMonth: 'MMM dd',
  long: 'EEEE, MMMM d, yyyy',
  dateTime: 'MMM d, yyyy HH:mm',
}

const INTL: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  short: { year: 'numeric', month: 'short', day: 'numeric' },
  shortNoYear: { month: 'short', day: 'numeric' },
  dayMonth: { month: 'short', day: '2-digit' },
  long: { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
  dateTime: {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  },
}

/** The app's locale ids are 'en' / 'zh' (+ fr/ja/es); Intl wants a BCP-47 tag. */
export const intlLocale = (locale: string | undefined) =>
  locale === 'zh' ? 'zh-CN' : locale || 'en'

export const formatDateLocale = (date: Date, style: DateStyle, locale: string | undefined) => {
  if (!locale || locale === 'en' || locale.startsWith('en-')) return format(date, DATE_FNS[style])
  return new Intl.DateTimeFormat(intlLocale(locale), INTL[style]).format(date)
}

/** `fmt(date, 'short')` bound to the current locale. */
export const useDateFormatter = () => {
  const locale = useLocale()
  return useCallback(
    (date: Date, style: DateStyle = 'short') => formatDateLocale(date, style, locale),
    [locale],
  )
}
